import React, { useState, useRef, useEffect } from 'react';
import { Bot, Network, Globe, Box, GraduationCap, Smartphone, ChevronRight, X } from 'lucide-react';

const services = [
    {
        id: 'ai-automation',
        title: 'AI & Automation',
        summary: 'Custom machine learning models and intelligent decision systems.',
        details: 'We engineer proprietary machine learning models, predictive analytics, and autonomous workflow agents tailored to your enterprise data. Automate complex operational pipelines with real-time decision intelligence.',
        icon: <Bot size={28} />
    },
    {
        id: 'api-dev',
        title: 'API Development',
        summary: 'Secure, scalable APIs to connect your platforms and products.',
        details: 'High-performance RESTful and GraphQL architecture with enterprise-grade authentication, rate limiting, and seamless third-party integrations. Built for zero downtime and maximum developer velocity.',
        icon: <Network size={28} />
    },
    {
        id: 'web-solution',
        title: 'Web Solution',
        summary: 'Responsive, scalable websites optimized for user engagement.',
        details: 'Bespoke, high-converting digital web applications built with modern frontend frameworks. Optimized for ultra-fast performance, SEO dominance, and responsive perfection across all devices.',
        icon: <Globe size={28} />
    },
    {
        id: '3d-experiences',
        title: '3D Experiences',
        summary: 'Immersive WebGL and Three.js brand storytelling.',
        details: 'Interactive 3D web environments, shader animations, and visual storytelling crafted with WebGL and Three.js. Capture user attention and showcase products in immersive digital spaces.',
        icon: <Box size={28} />
    },
    {
        id: 'ai-training',
        title: 'AI Training',
        summary: 'Personalized AI and machine learning courses for teams.',
        details: 'Comprehensive corporate workshops and hands-on technical training for engineering teams and executives. Learn model fine-tuning, prompt engineering, and agentic framework integration.',
        icon: <GraduationCap size={28} />
    },
    {
        id: 'app-dev',
        title: 'App Development',
        summary: 'High-performance iOS and Android mobile applications.',
        details: 'End-to-end mobile app engineering using native and cross-platform technologies. Features real-time state synchronization, biometric security, offline storage, and polished micro-interactions.',
        icon: <Smartphone size={28} />
    }
];

const Services = () => {
    // Duplicate services array for seamless continuous scrolling
    const marqueeServices = [...services, ...services];

    const [expandedIndex, setExpandedIndex] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeftPos, setScrollLeftPos] = useState(0);

    const scrollContainerRef = useRef(null);
    const timerRef = useRef(null);

    const closeExpanded = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setExpandedIndex(null);
    };

    const handleLearnMore = (index, e) => {
        e.stopPropagation();

        if (expandedIndex === index) {
            closeExpanded();
            return;
        }

        // Clear any existing timer
        if (timerRef.current) clearTimeout(timerRef.current);

        setExpandedIndex(index);

        // 25-second auto-close timer
        timerRef.current = setTimeout(() => {
            closeExpanded();
        }, 25000);
    };

    // Mouse Drag to Slide Left & Right
    const handleMouseDown = (e) => {
        if (!scrollContainerRef.current) return;
        setIsMouseDown(true);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeftPos(scrollContainerRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsMouseDown(false);
        setIsHovered(false);
    };

    const handleMouseUp = () => {
        setIsMouseDown(false);
    };

    const handleMouseMove = (e) => {
        if (!isMouseDown || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 1.8; // Smooth slide speed
        const container = scrollContainerRef.current;
        let newScrollLeft = scrollLeftPos - walk;

        // Bidirectional seamless boundary wrapping
        const halfWidth = container.scrollWidth / 2;
        if (newScrollLeft >= halfWidth) {
            newScrollLeft -= halfWidth;
            setStartX(e.pageX - container.offsetLeft);
            setScrollLeftPos(newScrollLeft);
        } else if (newScrollLeft <= 0) {
            newScrollLeft += halfWidth;
            setStartX(e.pageX - container.offsetLeft);
            setScrollLeftPos(newScrollLeft);
        }

        container.scrollLeft = newScrollLeft;
    };

    const handleNativeScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const halfWidth = container.scrollWidth / 2;
        if (container.scrollLeft >= halfWidth + 100) {
            container.scrollLeft -= halfWidth;
        } else if (container.scrollLeft <= 0) {
            container.scrollLeft += halfWidth;
        }
    };

    // Continuous Auto-Scroll Engine (pauses when user drags, hovers, or expands card)
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        let animFrameId;

        const autoScrollStep = () => {
            if (!isHovered && !isMouseDown && expandedIndex === null) {
                const halfWidth = container.scrollWidth / 2;
                if (container.scrollLeft >= halfWidth) {
                    container.scrollLeft -= halfWidth;
                } else {
                    container.scrollLeft += 0.75;
                }
            }
            animFrameId = requestAnimationFrame(autoScrollStep);
        };

        animFrameId = requestAnimationFrame(autoScrollStep);

        return () => {
            cancelAnimationFrame(animFrameId);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isHovered, isMouseDown, expandedIndex]);

    return (
        <section id="services" style={{ padding: '9rem 0', backgroundColor: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: '100%' }}>

                {/* Section Header */}
                <div className="container reveal" style={{ marginBottom: '3.5rem', textAlign: 'center' }}>
                    <span className="section-label section-label-light">
                        Our Expertise
                    </span>
                    <h2 style={{
                        fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                        fontWeight: 800,
                        marginBottom: '0.75rem',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                        color: 'var(--accent-slate)'
                    }}>
                        Our <span style={{ color: 'var(--text-muted)' }}>Expertise.</span>
                    </h2>
                    <p style={{
                        color: 'var(--text-muted)',
                        fontSize: '1.2rem',
                        fontWeight: 300,
                        maxWidth: '560px',
                        margin: '0 auto'
                    }}>
                        Precision-engineered solutions for the modern enterprise.
                    </p>
                </div>

                {/* Services Slide / Drag Carousel Container */}
                <div 
                    className={`services-scroll-container ${isMouseDown ? 'dragging' : ''}`}
                    ref={scrollContainerRef}
                    onScroll={handleNativeScroll}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={handleMouseLeave}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    style={{
                        display: 'flex',
                        width: '100%',
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        WebkitOverflowScrolling: 'touch',
                        padding: '1rem 0 2rem',
                        transitionDelay: '0.2s',
                        cursor: isMouseDown ? 'grabbing' : 'grab',
                        userSelect: isMouseDown ? 'none' : 'auto'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        gap: '2rem',
                        paddingLeft: 'max(1.5rem, calc((100vw - 1200px) / 2))',
                        paddingRight: '2rem',
                        alignItems: 'stretch'
                    }}>
                        {marqueeServices.map((service, index) => {
                            const isExpanded = expandedIndex === index;

                            return (
                                <div
                                    key={index}
                                    className={`glass-card hover-glow service-card ${isExpanded ? 'expanded' : ''}`}
                                    style={{
                                        borderRadius: '24px',
                                        padding: isExpanded ? '2.5rem 2.25rem' : '2.25rem 2rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: isExpanded ? '460px' : '380px',
                                        flexShrink: 0,
                                        position: 'relative',
                                        background: isExpanded 
                                            ? 'linear-gradient(145deg, #FFFFFF 0%, #F1F5F9 100%)' 
                                            : 'linear-gradient(145deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.55) 100%)',
                                        border: isExpanded 
                                            ? '1px solid rgba(59, 130, 246, 0.4)' 
                                            : '1px solid rgba(255,255,255,0.6)',
                                        boxShadow: isExpanded 
                                            ? '0 25px 60px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.8)' 
                                            : '0 10px 30px rgba(0, 0, 0, 0.04)',
                                        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                                        minHeight: '230px',
                                        zIndex: isExpanded ? 30 : 1
                                    }}
                                >
                                    {/* Top 25s Animated Slide Progress Bar & Close Button */}
                                    {isExpanded && (
                                        <>
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: '4px',
                                                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                                borderTopLeftRadius: '24px',
                                                borderTopRightRadius: '24px',
                                                overflow: 'hidden'
                                            }}>
                                                <div 
                                                    key={expandedIndex}
                                                    style={{
                                                        height: '100%',
                                                        background: 'linear-gradient(90deg, #3B82F6 0%, #2563EB 100%)',
                                                        width: '100%',
                                                        animation: 'slideBarTimer 25s linear forwards'
                                                    }}
                                                />
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                alignItems: 'center',
                                                marginBottom: '0.75rem'
                                            }}>
                                                <button
                                                    onClick={closeExpanded}
                                                    style={{
                                                        background: 'rgba(0,0,0,0.05)',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '28px',
                                                        height: '28px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        color: 'var(--text-muted)',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    title="Close and resume slideshow"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </>
                                    )}

                                    {/* Icon & Title */}
                                    <div style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '16px',
                                        background: isExpanded 
                                            ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' 
                                            : 'linear-gradient(135deg, var(--bg-secondary) 0%, #E2E8F0 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: isExpanded ? '#FFFFFF' : 'var(--accent-slate)',
                                        marginBottom: '1.25rem',
                                        boxShadow: isExpanded 
                                            ? '0 8px 20px rgba(59, 130, 246, 0.3)' 
                                            : 'inset 0 2px 4px 0 rgba(255, 255, 255, 0.7)',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        {service.icon}
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <h3 style={{
                                            fontSize: '1.35rem',
                                            fontWeight: 700,
                                            marginBottom: '0.75rem',
                                            letterSpacing: '-0.02em',
                                            color: 'var(--accent-slate)'
                                        }}>
                                            {service.title}
                                        </h3>

                                        <p style={{
                                            color: 'var(--text-muted)',
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem',
                                            marginBottom: isExpanded ? '1.25rem' : '1.5rem'
                                        }}>
                                            {service.summary}
                                        </p>

                                        {/* Expanded Detailed Breakdown */}
                                        {isExpanded && (
                                            <div style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                                borderRadius: '16px',
                                                padding: '1.25rem',
                                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                                marginBottom: '1.5rem',
                                                fontSize: '0.92rem',
                                                color: 'var(--text-main)',
                                                lineHeight: 1.7,
                                                animation: 'fadeIn 0.3s ease-out'
                                            }}>
                                                {service.details}
                                            </div>
                                        )}

                                        {/* Learn More / Action Button */}
                                        <div style={{ marginTop: 'auto' }}>
                                            <button
                                                onClick={(e) => handleLearnMore(index, e)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.4rem',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 700,
                                                    color: isExpanded ? '#3B82F6' : 'var(--text-muted)',
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    transition: 'all 0.2s ease',
                                                    letterSpacing: '0.02em'
                                                }}
                                                className="learn-more-btn"
                                            >
                                                <span>{isExpanded ? 'Close Details' : 'Learn More'}</span>
                                                <ChevronRight 
                                                    size={15} 
                                                    style={{ 
                                                        transform: isExpanded ? 'rotate(90deg)' : 'none',
                                                        transition: 'transform 0.3s ease'
                                                    }} 
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <style>{`
          .services-scroll-container::-webkit-scrollbar {
             display: none;
          }

          .services-scroll-container.dragging {
             scroll-behavior: auto !important;
          }

          @keyframes slideBarTimer {
            0% { width: 100%; }
            100% { width: 0%; }
          }

          .glass-card:hover {
             transform: translateY(-5px);
             z-index: 10;
          }

          .learn-more-btn:hover {
             color: #3B82F6 !important;
          }

          .learn-more-btn:hover svg {
             transform: translateX(3px);
          }

          @media (max-width: 600px) {
             .service-card {
                 width: 320px !important;
                 padding: 1.75rem 1.5rem !important;
             }
             .service-card.expanded {
                 width: 340px !important;
             }
          }
        `}</style>

            </div>
        </section>
    );
};

export default Services;
